/**
 * "multi-ref" badges for identity-referenced i2v scenes (DCR-002).
 *
 * Two surfaces, one hover card:
 * - `IdentityReferenceSummaryBadge` sits in Director View's badge row and
 *   answers "which shots went multi-reference" for the whole echo;
 * - `IdentityReferenceSceneBadge` sits on a single scene card in the
 *   Cinematic-animation section and answers it for that scene.
 *
 * The hover shows who each reference belongs to (the cast subject — person or
 * dog — with its cast descriptor) and the actual uploaded photos that were sent
 * to the reference-to-video graph, signed on demand like every other CRM
 * thumbnail. Signing happens only when the card opens, so the badge itself
 * costs nothing.
 */

import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import type { EchoflicksSessionMedia } from "../types";
import { mouthLockV2Label } from "./mouthLock";
import type {
  IdentityReferenceScene,
  IdentityReferenceSubject,
} from "./identityReferences";
import { useMediaUrl } from "./useMediaUrl";

const ReferenceThumb = ({
  file,
  media,
}: {
  file: string;
  media?: EchoflicksSessionMedia;
}) => {
  const url = useMediaUrl(
    media
      ? {
          url: media.url,
          storage_bucket: media.storage_bucket,
          storage_path: media.storage_path,
          storage_is_public: media.storage_is_public ?? false,
        }
      : null,
  );
  const [failed, setFailed] = useState(false);
  return (
    <figure className="w-16 shrink-0 space-y-0.5" title={file}>
      <div className="flex aspect-square items-center justify-center overflow-hidden rounded border bg-muted">
        {url.url && !failed ? (
          <img
            src={url.url}
            alt={file}
            className="size-full object-cover"
            onError={() => setFailed(true)}
          />
        ) : (
          <span className="px-1 text-center text-[9px] leading-tight text-muted-foreground">
            {failed ? "unavailable" : "…"}
          </span>
        )}
      </div>
      <figcaption className="truncate text-[10px] text-muted-foreground">
        {file}
      </figcaption>
    </figure>
  );
};

const SubjectRow = ({ subject }: { subject: IdentityReferenceSubject }) => (
  <div data-testid="identity-reference-subject">
    <div className="text-xs font-medium">
      {subject.kind === "dog" ? "Dog" : "Person"}
      {subject.descriptor ? (
        <span className="ml-1 font-normal text-muted-foreground">
          {subject.descriptor}
        </span>
      ) : null}
    </div>
    {subject.mouthLockV2 ? (
      <div
        data-testid="identity-reference-mouth-lock"
        className="text-[11px] text-muted-foreground"
      >
        Mouth: {mouthLockV2Label(subject.mouthLockV2)}
      </div>
    ) : null}
    <div className="mt-1 flex flex-wrap gap-1.5">
      {subject.references.map((reference, index) => (
        <ReferenceThumb
          key={`${reference.file}:${index}`}
          file={reference.file}
          media={reference.media}
        />
      ))}
    </div>
  </div>
);

const SceneDetail = ({
  scene,
  withHeading,
}: {
  scene: IdentityReferenceScene;
  withHeading: boolean;
}) => (
  <div className="space-y-2">
    {withHeading ? (
      <div className="text-xs font-semibold">
        Scene {scene.sceneId ?? "?"}
        {scene.sourceFile ? (
          <span className="ml-1 font-normal text-muted-foreground">
            {scene.sourceFile}
          </span>
        ) : null}
      </div>
    ) : null}
    {scene.subjects.map((subject, index) => (
      <SubjectRow
        key={`${subject.groupId ?? "subject"}:${index}`}
        subject={subject}
      />
    ))}
  </div>
);

const HOVER_EXPLAINER =
  "These uploaded photos rode along to the video model as identity references, so the clip keeps this subject looking like themselves.";

/**
 * One badge for the Director View badge row: every multi-reference scene of
 * the echo, hover for the cast subjects and the photos used.
 */
export function IdentityReferenceSummaryBadge({
  scenes,
}: {
  scenes: IdentityReferenceScene[];
}) {
  if (scenes.length === 0) return null;
  const references = scenes.reduce(
    (sum, scene) => sum + scene.referenceCount,
    0,
  );
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          data-testid="identity-reference-summary-badge"
          variant="outline"
          className="cursor-default border-violet-500 text-violet-600"
        >
          multi-ref · {scenes.length} scene{scenes.length === 1 ? "" : "s"}
        </Badge>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        className="max-h-80 w-96 space-y-3 overflow-y-auto bg-popover text-popover-foreground shadow-md"
      >
        <p className="text-[11px] text-muted-foreground">
          {HOVER_EXPLAINER} {references} reference
          {references === 1 ? "" : "s"} across {scenes.length} scene
          {scenes.length === 1 ? "" : "s"}.
        </p>
        {scenes.map((scene, index) => (
          <SceneDetail
            key={scene.sceneKey ?? `scene:${index}`}
            scene={scene}
            withHeading
          />
        ))}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The per-scene variant for the Cinematic-animation section's scene cards.
 */
export function IdentityReferenceSceneBadge({
  scene,
}: {
  scene: IdentityReferenceScene | undefined;
}) {
  if (!scene) return null;
  const dogs = scene.subjects.filter(
    (subject) => subject.kind === "dog",
  ).length;
  const people = scene.subjects.length - dogs;
  const who = [
    people > 0 ? `${people} person${people === 1 ? "" : "s"}` : null,
    dogs > 0 ? `${dogs} dog${dogs === 1 ? "" : "s"}` : null,
  ]
    .filter(Boolean)
    .join(" + ");
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          data-testid="identity-reference-scene-badge"
          variant="outline"
          className="cursor-default border-violet-500 text-violet-600"
        >
          multi-ref · {scene.referenceCount}
        </Badge>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        // z-[60] beats the fullscreen Director View dialog, which shares the
        // tooltip's own z-50: this badge renders inside that dialog, and
        // depending on portal insertion order for a hover card is fragile.
        className="z-[60] w-96 space-y-2 bg-popover text-popover-foreground shadow-md"
      >
        <p className="text-[11px] text-muted-foreground">
          {HOVER_EXPLAINER} {who}
          {scene.citationMode === "cite"
            ? "; each reference is cited to its subject in the prompt."
            : "."}
        </p>
        <SceneDetail scene={scene} withHeading={false} />
      </TooltipContent>
    </Tooltip>
  );
}
